import { useState } from "react";
import { Settings2, Cpu, Target, Factory, Users, ClipboardList, ChevronDown, ChevronUp } from "lucide-react";
import useScrollReveal from "../hooks/useScrollReveal";
import { useLanguage } from "../i18n/LanguageContext";

const icons = [Settings2, Cpu, Target, Factory, Users, ClipboardList];

function ServiceCard({ service, icon: Icon, index, isVisible, expandLabel, collapseLabel }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div
      data-testid={`service-card-${index}`}
      onClick={() => setExpanded(!expanded)}
      className="card-hover-lift bg-[#162A45] border border-slate-800 p-8 hover:border-[#D4AF37]/40 group relative overflow-hidden cursor-pointer select-none"
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translateY(0) scale(1)" : "translateY(32px) scale(0.97)",
        transition: "opacity 0.8s cubic-bezier(0.16,1,0.3,1), transform 0.8s cubic-bezier(0.16,1,0.3,1), border-color 0.5s, box-shadow 0.4s",
        transitionDelay: `${index * 120}ms`,
      }}
    >
      <div className="absolute top-0 left-0 h-px w-16 bg-[#D4AF37] group-hover:w-full transition-all duration-500" />

      <div className="flex items-start gap-4 mb-4">
        <div className="w-12 h-12 bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#D4AF37]/20 transition-colors duration-300">
          <Icon size={22} className="text-[#D4AF37]" strokeWidth={1.5} />
        </div>
        <div className="flex-1 min-w-0">
          <span className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-medium">{service.subtitle}</span>
          <h3 className="text-white mt-1 leading-snug text-base md:text-lg" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            {service.title}
          </h3>
        </div>
      </div>

      <div
        className="overflow-hidden transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]"
        style={{ maxHeight: expanded ? "400px" : "0", opacity: expanded ? 1 : 0 }}
      >
        <p className="text-slate-300 text-sm leading-relaxed mb-5">{service.description}</p>
        <ul className="space-y-2">
          {service.points.map((point, i) => (
            <li key={i} className="flex items-start gap-2 text-slate-300 text-xs">
              <div className="w-1 h-1 rounded-full bg-[#D4AF37] mt-1.5 flex-shrink-0" />
              {point}
            </li>
          ))}
        </ul>
      </div>

      <div className="flex items-center gap-2 mt-4 text-[#D4AF37] text-[11px] uppercase tracking-widest">
        {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        <span>{expanded ? collapseLabel : expandLabel}</span>
      </div>
    </div>
  );
}

export default function Services() {
  const [ref, isVisible] = useScrollReveal();
  const { t } = useLanguage();
  const s = t.services;

  return (
    <section id="services" data-testid="services-section" ref={ref} className="bg-[#0F1A2E] py-24 md:py-32 relative overflow-hidden">
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#D4AF37]/[0.03] rounded-full blur-[120px] pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-end mb-16">
          <div className="transition-all duration-700" style={{ opacity: isVisible ? 1 : 0, transform: isVisible ? "translateY(0)" : "translateY(20px)" }}>
            <div className="flex items-center gap-3 mb-4">
              <div className="h-px w-12 bg-[#D4AF37]" />
              <span className="text-[#D4AF37] text-xs uppercase tracking-widest">{s.label}</span>
            </div>
            <h2 className="text-4xl md:text-5xl text-white max-w-2xl leading-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              {s.heading}
            </h2>
            <p className="text-slate-300 mt-4 max-w-2xl text-base leading-relaxed">{s.desc}</p>
          </div>
          <div className="hidden lg:block transition-all duration-700 delay-200" style={{ opacity: isVisible ? 1 : 0, transform: isVisible ? "translateX(0)" : "translateX(20px)" }}>
            <div className="relative h-48 overflow-hidden">
              <img
                src="https://images.pexels.com/photos/2760241/pexels-photo-2760241.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Industrial operations"
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-[#0B1221] via-[#0B1221]/60 to-transparent" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B1221] via-transparent to-[#0B1221]/40" />
              <div className="absolute bottom-0 left-0 right-0 h-px bg-[#D4AF37]/20" />
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          {s.items.slice(0, 3).map((svc, i) => (
            <ServiceCard key={i} service={svc} icon={icons[i]} index={i} isVisible={isVisible} expandLabel={s.expandLabel} collapseLabel={s.collapseLabel} />
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {s.items.slice(3).map((svc, i) => (
            <ServiceCard key={i + 3} service={svc} icon={icons[i + 3]} index={i + 3} isVisible={isVisible} expandLabel={s.expandLabel} collapseLabel={s.collapseLabel} />
          ))}
        </div>
      </div>
    </section>
  );
}
